import React, { useState, useEffect } from 'react';
import { FaHistory, FaSpinner, FaExclamationTriangle, FaFilter, FaSearch } from 'react-icons/fa';
import { activityLogAPI } from '../../services/api';

const LogViewer = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const response = await activityLogAPI.getRecentActivity();
            const activityLogs = Array.isArray(response) ? response : 
                               Array.isArray(response.activities) ? response.activities :
                               Array.isArray(response.logs) ? response.logs : [];
            setLogs(activityLogs);
            setError(null);
        } catch (err) {
            setError('Failed to fetch activity logs');
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    const getLogTypeColor = (type) => {
        const colors = {
            project_created: 'bg-blue-100 text-blue-800',
            project_updated: 'bg-blue-100 text-blue-800',
            member_updated: 'bg-green-100 text-green-800',
            member_deleted: 'bg-red-100 text-red-800',
            event_created: 'bg-purple-100 text-purple-800',
            event_updated: 'bg-purple-100 text-purple-800',
            academy_application_submitted: 'bg-yellow-100 text-yellow-800',
            academy_application_updated: 'bg-yellow-100 text-yellow-800',
            partnership_application_submitted: 'bg-indigo-100 text-indigo-800',
            partnership_application_updated: 'bg-indigo-100 text-indigo-800',
            group_created: 'bg-pink-100 text-pink-800',
            group_member_added: 'bg-pink-100 text-pink-800'
        };
        return colors[type] || 'bg-gray-100 text-gray-800';
    };

    const getLogTypeIcon = (type) => {
        const icons = {
            project_created: '📁',
            project_updated: '📝',
            member_updated: '👤',
            member_deleted: '🗑️',
            event_created: '📅',
            event_updated: '📅',
            academy_application_submitted: '🎓',
            academy_application_updated: '🎓',
            partnership_application_submitted: '🤝',
            partnership_application_updated: '🤝',
            group_created: '👥',
            group_member_added: '👥'
        };
        return icons[type] || '📋';
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const filterLogs = () => {
        if (!Array.isArray(logs)) return [];
        
        return logs.filter(log => {
            if (!log) return false;
            
            const matchesType = selectedType === 'all' || log.type === selectedType;
            const matchesSearch = searchTerm === '' || 
                JSON.stringify(log).toLowerCase().includes(searchTerm.toLowerCase());
            return matchesType && matchesSearch;
        });
    };

    const renderLogDetails = (log) => {
        if (!log) return null;

        const renderValue = (value) => {
            if (value === null || value === undefined) return 'N/A';
            if (typeof value === 'object') {
                return (
                    <div className="log-viewer__nested-details">
                        {Object.entries(value).map(([key, val]) => {
                            // Skip redundant fields
                            if (['id', 'createdAt', 'timestamp'].includes(key)) return null;
                            return (
                                <div key={key} className="log-viewer__detail-item">
                                    <span className="log-viewer__detail-label">{key}:</span>
                                    <span className="log-viewer__detail-value">
                                        {typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                );
            }
            return String(value);
        };

        return (
            <div className="log-viewer__details">
                {/* Subject Information */}
                {log.subject && (
                    <div className="log-viewer__detail-section">
                        <h4 className="log-viewer__section-title">Subject Details</h4>
                        {log.subject.type && (
                            <div className="log-viewer__detail-item">
                                <span className="log-viewer__detail-label">Type:</span>
                                <span className="log-viewer__detail-value">{log.subject.type}</span>
                            </div>
                        )}
                        {log.subject.title && (
                            <div className="log-viewer__detail-item">
                                <span className="log-viewer__detail-label">Title:</span>
                                <span className="log-viewer__detail-value">{log.subject.title}</span>
                            </div>
                        )}
                        {log.subject.details && (
                            <div className="log-viewer__detail-item">
                                <span className="log-viewer__detail-label">Details:</span>
                                <div className="log-viewer__detail-value">
                                    {renderValue(log.subject.details)}
                                </div>
                            </div>
                        )}
                        {log.subject.changes && (
                            <div className="log-viewer__detail-item">
                                <span className="log-viewer__detail-label">Changes:</span>
                                <div className="log-viewer__detail-value">
                                    {renderValue(log.subject.changes)}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Additional Fields */}
                {Object.entries(log).map(([key, value]) => {
                    // Skip fields that are already shown in the table columns or are redundant
                    if (['type', 'action', 'performedBy', 'timestamp', 'subject', 'id', 'createdAt'].includes(key)) {
                        return null;
                    }
                    return (
                        <div key={key} className="log-viewer__detail-section">
                            <h4 className="log-viewer__section-title">{key}</h4>
                            <div className="log-viewer__detail-item">
                                <div className="log-viewer__detail-value">
                                    {renderValue(value)}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="log-viewer__loading">
                <FaSpinner className="log-viewer__spinner" />
                <p>Loading activity logs...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="log-viewer__error">
                <FaExclamationTriangle className="log-viewer__error-icon" />
                <p>{error}</p>
            </div>
        );
    }

    const filteredLogs = filterLogs();
    const uniqueTypes = [...new Set(logs.map(log => log.type))];

    return (
        <div className="log-viewer">
            <div className="log-viewer__header">
                <div className="log-viewer__title">
                    <FaHistory className="log-viewer__title-icon" />
                    <h1>Activity Logs</h1>
                </div>
                <div className="log-viewer__filters">
                    <div className="log-viewer__search">
                        <FaSearch className="log-viewer__search-icon" />
                        <input
                            type="text"
                            placeholder="Search logs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="log-viewer__search-input"
                        />
                    </div>
                    <div className="log-viewer__type-filter">
                        <FaFilter className="log-viewer__filter-icon" />
                        <select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                            className="log-viewer__type-select"
                        >
                            <option value="all">All Types</option>
                            {uniqueTypes.map(type => (
                                <option key={type} value={type}>
                                    {type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="log-viewer__table-container">
                <table className="log-viewer__table">
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Action</th>
                            <th>Performed By</th>
                            <th>Timestamp</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLogs.map((log, index) => (
                            <tr key={index} className="log-viewer__row">
                                <td>
                                    <span className={`log-viewer__type-badge ${getLogTypeColor(log.type)}`}>
                                        {getLogTypeIcon(log.type)} {log.type.split('_').map(word => 
                                            word.charAt(0).toUpperCase() + word.slice(1)
                                        ).join(' ')}
                                    </span>
                                </td>
                                <td>{log.action}</td>
                                <td>
                                    <div className="log-viewer__user-info">
                                        <span className="log-viewer__user-name">{log.performedBy?.split('(')[0]?.trim()}</span>
                                        <span className="log-viewer__user-email">{log.performedBy?.match(/\((.*?)\)/)?.[1]}</span>
                                    </div>
                                </td>
                                <td>{formatTimestamp(log.timestamp)}</td>
                                <td className="log-viewer__details-cell">{renderLogDetails(log)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredLogs.length === 0 && (
                <div className="log-viewer__empty">
                    <p>No activity logs found</p>
                </div>
            )}
        </div>
    );
};

export default LogViewer;

const styles = `
.log-viewer {
    padding: 1.5rem;
    max-width: 80rem;
    margin: 0 auto;
}

.log-viewer__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 1rem;
}

.log-viewer__spinner {
    font-size: 2rem;
    color: var(--primary-color);
    animation: spin 1s linear infinite;
}

.log-viewer__error {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background-color: #FEF2F2;
    border-left: 4px solid #EF4444;
    border-radius: 0 0.5rem 0.5rem 0;
    color: #B91C1C;
}

.log-viewer__error-icon {
    font-size: 1.25rem;
    color: #F87171;
}

.log-viewer__header {
    margin-bottom: 2rem;
}

.log-viewer__title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
}

.log-viewer__title-icon {
    padding: 0.75rem;
    background-color: rgba(var(--primary-rgb), 0.1);
    border-radius: 0.5rem;
    color: var(--primary-color);
    font-size: 1.5rem;
}

.log-viewer__title h1 {
    font-size: 1.875rem;
    font-weight: 700;
    color: #111827;
}

.log-viewer__filters {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.log-viewer__search {
    position: relative;
    flex: 1;
}

.log-viewer__search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6B7280;
}

.log-viewer__search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid #E5E7EB;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    color: #111827;
    background-color: white;
    transition: border-color 0.15s ease-in-out;
}

.log-viewer__search-input:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
}

.log-viewer__type-filter {
    position: relative;
    min-width: 200px;
}

.log-viewer__filter-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6B7280;
}

.log-viewer__type-select {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid #E5E7EB;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    color: #111827;
    background-color: white;
    appearance: none;
    cursor: pointer;
    transition: border-color 0.15s ease-in-out;
}

.log-viewer__type-select:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
}

.log-viewer__table-container {
    background-color: white;
    border-radius: 1rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid #F3F4F6;
    overflow: hidden;
}

.log-viewer__table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.log-viewer__table th {
    padding: 1rem 1.5rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6B7280;
    background-color: #F9FAFB;
    border-bottom: 1px solid #E5E7EB;
}

.log-viewer__table td {
    padding: 1rem 1.5rem;
    font-size: 0.875rem;
    color: #4B5563;
    border-bottom: 1px solid #E5E7EB;
}

.log-viewer__row:hover {
    background-color: #F9FAFB;
}

.log-viewer__type-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 9999px;
}

.log-viewer__details {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.log-viewer__detail-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.log-viewer__detail-label {
    font-weight: 600;
    color: #6B7280;
}

.log-viewer__detail-value {
    color: #111827;
}

.log-viewer__empty {
    text-align: center;
    padding: 4rem 2rem;
    color: #6B7280;
}

.log-viewer__details-cell {
    max-width: 600px;
    min-width: 400px;
}

.log-viewer__detail-section {
    margin-bottom: 1rem;
    padding: 0.75rem;
    background-color: #F9FAFB;
    border-radius: 0.5rem;
}

.log-viewer__section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
    padding-bottom: 0.25rem;
    border-bottom: 1px solid #E5E7EB;
}

.log-viewer__nested-details {
    margin-left: 1rem;
    padding-left: 1rem;
    border-left: 2px solid #E5E7EB;
}

.log-viewer__detail-value {
    word-break: break-word;
    white-space: pre-wrap;
}

.log-viewer__detail-value pre {
    margin: 0;
    padding: 0.5rem;
    background-color: #F3F4F6;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    overflow-x: auto;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.log-viewer__user-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.log-viewer__user-name {
    font-weight: 600;
    color: #111827;
}

.log-viewer__user-email {
    font-size: 0.75rem;
    color: #6B7280;
}
`;

// Add styles to document
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
}